import { Room, Network, PCInstance, PCSyncStatus, ConfigurationRelease } from '../../types/database';

interface RoomSyncStatusProps {
  rooms: Room[];
  networks: Network[];
  pcInstances: PCInstance[];
  syncStatuses: PCSyncStatus[];
  configReleases: ConfigurationRelease[];
  onTriggerRoomUpdate: (roomId: string, pcIds: string[]) => void;
  onTriggerPCUpdate: (pcInstanceId: string) => void;
  // Sticky sync pause (swarm): per-PC toggle + room-wide batch.
  onSetPCSyncPaused?: (pc: PCInstance, paused: boolean) => void;
  onSetRoomSyncPaused?: (roomId: string, pcs: PCInstance[], paused: boolean) => void;
  readOnly?: boolean;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

export default function RoomSyncStatus({
  rooms,
  networks,
  pcInstances,
  syncStatuses,
  configReleases,
  onTriggerRoomUpdate,
  onTriggerPCUpdate,
  onSetPCSyncPaused,
  onSetRoomSyncPaused,
  readOnly = false,
}: RoomSyncStatusProps) {
  if (rooms.length === 0) {
    return (
      <div className="room-sync-panel">
        <div className="room-sync-header">Room Sync Status</div>
        <div className="room-sync-empty">Select a configuration to view sync status</div>
      </div>
    );
  }

  // Build a lookup: (pc_instance_id, configuration_release_id) -> PCSyncStatus
  const syncLookup = new Map<string, PCSyncStatus>();
  for (const ss of syncStatuses) {
    syncLookup.set(`${ss.pc_instance_id}:${ss.configuration_release_id}`, ss);
  }

  // Group PCs by network
  const pcsByNetwork = new Map<string, PCInstance[]>();
  for (const pc of pcInstances) {
    if (!pcsByNetwork.has(pc.network_id)) {
      pcsByNetwork.set(pc.network_id, []);
    }
    pcsByNetwork.get(pc.network_id)!.push(pc);
  }

  // Map networks by room
  const networksByRoom = new Map<string, Network[]>();
  for (const net of networks) {
    if (!networksByRoom.has(net.room_id)) {
      networksByRoom.set(net.room_id, []);
    }
    networksByRoom.get(net.room_id)!.push(net);
  }

  return (
    <div className="room-sync-panel">
      <div className="room-sync-header">Room Sync Status</div>
      <div className="room-sync-scroll">
        {rooms.map((room) => {
          const roomNetworks = networksByRoom.get(room.id) || [];
          const roomPCs = roomNetworks.flatMap(
            (net) => pcsByNetwork.get(net.id) || []
          );

          return (
            <div key={room.id} className="room-group">
              <div className="room-group-header">
                <span className="room-group-title">{room.name}</span>
                {!readOnly && (
                  <>
                    <button
                      className="deployment-btn deployment-btn-primary deployment-btn-sm"
                      onClick={() =>
                        onTriggerRoomUpdate(
                          room.id,
                          roomPCs.map((pc) => pc.id)
                        )
                      }
                    >
                      Update All PCs
                    </button>
                    {onSetRoomSyncPaused && (
                      roomPCs.some((pc) => pc.sync_paused) ? (
                        <button
                          className="deployment-btn deployment-btn-sm sync-resume-btn"
                          onClick={() => onSetRoomSyncPaused(room.id, roomPCs, false)}
                        >
                          ▶ Resume all syncing
                        </button>
                      ) : (
                        <button
                          className="deployment-btn deployment-btn-sm sync-pause-btn"
                          onClick={() => onSetRoomSyncPaused(room.id, roomPCs, true)}
                        >
                          ⏸ Pause all syncing
                        </button>
                      )
                    )}
                  </>
                )}
              </div>

              {roomPCs
                .sort((a, b) => {
                  if (a.role === 'primary' && b.role !== 'primary') return -1;
                  if (a.role !== 'primary' && b.role === 'primary') return 1;
                  return (a.pc_name || '').localeCompare(b.pc_name || '');
                })
                .map((pc) => {
                  const isOnline = pc.status === 'online';

                  return (
                    <div key={pc.id} className="pc-row">
                      <span className={`pc-status-dot ${isOnline ? 'pc-status-online' : 'pc-status-offline'}`} />
                      <span className="pc-name">{pc.pc_name || pc.id.slice(0, 8)}</span>
                      <span className={`pc-role-badge pc-role-${pc.role}`}>{pc.role}</span>
                      {pc.sync_paused && (
                        <span className="sync-paused-badge" title={pc.sync_paused_at ? `paused ${new Date(pc.sync_paused_at).toLocaleString()}` : undefined}>
                          ⏸ PAUSED{pc.sync_paused_by ? ` by ${pc.sync_paused_by}` : ''}
                        </span>
                      )}

                      <div className="pc-sync-statuses">
                        {configReleases.length === 0 && (
                          <span style={{ color: '#475569', fontSize: '0.7rem' }}>No releases assigned</span>
                        )}
                        {configReleases.map((cr) => {
                          const ss = syncLookup.get(`${pc.id}:${cr.id}`);
                          const expName = cr.experiences?.name || '?';
                          const version = cr.experience_release?.version || '';
                          const phase = cr.phase;
                          const status = ss?.sync_status || null;

                          return (
                            <div key={cr.id} className="pc-sync-item">
                              <span className="pc-sync-exp-name">
                                {expName}{version ? ` v${version}` : ''}
                              </span>
                              <span className={`phase-badge phase-${phase}`} style={{ fontSize: '0.55rem' }}>{phase}</span>
                              {status ? (
                                <span className={`sync-status-badge sync-${status}`}>
                                  {status}
                                </span>
                              ) : (
                                <span className="sync-status-badge sync-not-synced">not synced</span>
                              )}
                              {(status === 'downloading' || status === 'paused') && ss && (
                                <>
                                  <div className="sync-progress-bar">
                                    <div
                                      className="sync-progress-fill"
                                      style={{ width: `${ss.download_percentage || 0}%` }}
                                    />
                                  </div>
                                  <span className="sync-progress-text">
                                    {ss.download_percentage || 0}%
                                    {ss.total_bytes
                                      ? ` (${formatBytes(ss.bytes_downloaded)} / ${formatBytes(ss.total_bytes)})`
                                      : ''}
                                  </span>
                                </>
                              )}
                              {ss?.download_source && (
                                <span className="sync-source">
                                  {ss.download_source}
                                  {ss.download_source === 'swarm' && ss.sync_detail?.peers != null
                                    ? ` · ${ss.sync_detail.peers} peers${ss.sync_detail.seeding_to ? ` · seeding ${ss.sync_detail.seeding_to}` : ''}`
                                    : ''}
                                </span>
                              )}
                              {status === 'failed' && ss?.error_message && (
                                <span className="sync-error-msg">{ss.error_message}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {!readOnly && (
                        <div className="pc-actions">
                          <button
                            className="deployment-btn deployment-btn-sm"
                            onClick={() => onTriggerPCUpdate(pc.id)}
                            disabled={!!pc.sync_paused}
                            title={pc.sync_paused ? 'Sync paused — resume first' : undefined}
                          >
                            Trigger
                          </button>
                          {onSetPCSyncPaused && (
                            <button
                              className={`deployment-btn deployment-btn-sm ${pc.sync_paused ? 'sync-resume-btn' : 'sync-pause-btn'}`}
                              onClick={() => onSetPCSyncPaused(pc, !pc.sync_paused)}
                              title={pc.sync_paused ? 'Resume syncing on this PC' : 'Pause syncing on this PC (progress is kept)'}
                            >
                              {pc.sync_paused ? '▶' : '⏸'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
